<template>
  <div class="plans-comparison__wrap">
    <v-simple-table class="plans-comparison">
      <thead>
        <tr>
          <th class="plans-comparison__service">
            <div>Cobertura</div>
            <div class="text-caption plans-comparison__hint">Eventos al año × tope por evento</div>
          </th>
          <th
            v-for="(line, index) in lines"
            :key="index"
            :class="{ 'plans-comparison__custom': isCustom(line) }"
          >
            <div class="font-weight-bold">{{ line.label }}</div>
            <slot name="plan-actions" :line="line" />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Servicios digitales (app, televeterinaria, club, apadrinamiento)</td>
          <td v-for="(line, index) in lines" :key="index" :class="{ 'plans-comparison__custom': isCustom(line) }">
            Incluido
          </td>
        </tr>
        <tr v-for="code in serviceCodes" :key="code">
          <td>{{ serviceName(code) }}</td>
          <td
            v-for="(line, index) in lines"
            :key="index"
            class="plans-comparison__cell"
            :class="{ 'plans-comparison__custom': isCustom(line) }"
            :title="standardTitle(line, code)"
          >
            <template v-if="itemOf(line, code)">
              <span :class="{ 'plans-comparison__changed': change(line, code).events }">{{ itemOf(line, code).eventsPerYear }}</span>
              ×
              <span :class="{ 'plans-comparison__changed': change(line, code).cap }">{{ money(itemOf(line, code).cap) }}</span>
              <span v-if="arrow(line, code)" class="plans-comparison__arrow">{{ arrow(line, code) }}</span>
            </template>
            <span v-else class="plans-comparison__none">{{ standardOf(line, code) ? 'No incluye' : '—' }}</span>
          </td>
        </tr>
      </tbody>
    </v-simple-table>
  </div>
</template>

<script>
import { formatMoney } from '../utils/b2b-quotes.utils';
import { changeOf, coverageShort } from '../utils/partner-plans.utils';

/**
 * What each plan covers, side by side: one row per service, one column per
 * plan. Customizing a plan happens from its column header.
 */
export default {
  name: 'PlansComparison',
  props: {
    lines: { type: Array, default: () => [] },
    /** Catalog of the country: [{ code, name }]. */
    services: { type: Array, default: () => [] },
    currency: { type: String, default: 'CLP' }
  },
  computed: {
    /** Services any plan covers, in catalog order. */
    serviceCodes() {
      const covered = new Set(this.lines.flatMap((line) => line.items.map((item) => item.serviceCode)));
      const ordered = this.services.map((s) => s.code).filter((code) => covered.has(code));
      const extra = [...covered].filter((code) => !ordered.includes(code));
      return [...ordered, ...extra];
    }
  },
  methods: {
    /** A plan a medida: it changes services or starts from a broker variant. */
    isCustom(line) {
      return line.isCustom || Boolean(line.partnerVariantId);
    },
    itemOf(line, code) {
      return line.items.find((item) => item.serviceCode === code) ?? null;
    },
    /** The same service in the standard plan a custom one starts from, when that plan is quoted too. */
    standardOf(line, code) {
      if (!this.isCustom(line)) return null;
      const standard = this.lines.find((l) => !this.isCustom(l) && l.basePlan === line.basePlan);
      return standard ? this.itemOf(standard, code) : null;
    },
    change(line, code) {
      const item = this.itemOf(line, code);
      const standard = this.standardOf(line, code);
      if (!item || !standard) return { events: null, cap: null };
      return changeOf({ events: item.eventsPerYear, cap: item.cap }, standard);
    },
    /** ↑ when the custom plan covers more than the standard, ↓ when less, ↕ when one goes each way. */
    arrow(line, code) {
      const { events, cap } = this.change(line, code);
      const moves = new Set([events, cap].filter(Boolean));
      if (moves.size === 0) return '';
      if (moves.size === 2) return '↕';
      return moves.has('up') ? '↑' : '↓';
    },
    standardTitle(line, code) {
      const standard = this.standardOf(line, code);
      if (!standard) return null;
      return `Estándar: ${coverageShort(standard.eventsPerYear, this.money(standard.cap))}`;
    },
    serviceName(code) {
      return this.services.find((s) => s.code === code)?.name ?? code;
    },
    money(amount) {
      return formatMoney(amount, this.currency);
    }
  }
};
</script>

<style scoped>
.plans-comparison__service {
  min-width: 220px;
}
/* Every header cell starts at the same line, with room above and below;
   a custom plan's actions go under its name. */
.plans-comparison.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  vertical-align: top;
  padding-top: 14px;
  padding-bottom: 14px;
}
.plans-comparison__wrap {
  border-top: 1px solid var(--pw-border);
}
.plans-comparison__custom,
.plans-comparison.v-data-table > .v-data-table__wrapper > table > thead > tr > th.plans-comparison__custom {
  background: var(--pw-info-tint);
}
.plans-comparison__none {
  color: var(--pw-text-faint);
}
.plans-comparison__hint {
  color: var(--pw-text-faint);
  text-transform: none;
  letter-spacing: 0;
}
.plans-comparison__cell {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.plans-comparison__changed {
  font-weight: 600;
}
.plans-comparison__arrow {
  margin-left: 4px;
  color: var(--pw-text-muted);
}
</style>
